import { useState } from "react";
import { PHONE_RE, EMAIL_RE } from "@/components/booking/validation";

const formatPhone = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const FIELD_VALIDATORS = {
  owner_first_name: (v) => (v.trim() ? "" : "First name is required"),
  owner_last_name: (v) => (v.trim() ? "" : "Last name is required"),
  owner_phone: (v) => {
    if (!v) return "Phone number is required";
    return PHONE_RE.test(v) ? "" : "Use format 306-555-1234";
  },
  owner_email: (v) => {
    if (!v) return "Email is required";
    return EMAIL_RE.test(v) ? "" : "Enter a valid email address";
  },
  pet_name: (v) => (v.trim() ? "" : "Pet name is required"),
  pet_notes: (v) => (v.length > 300 ? "Notes must be 300 characters or less" : ""),
};

const validateField = (field, value) => (FIELD_VALIDATORS[field] ? FIELD_VALIDATORS[field](value) : "");

function TextField({ label, field, type = "text", placeholder, optional, autoComplete, value, error, onChange, onBlur }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium" style={{ color: "var(--pp-ink)" }} htmlFor={field}>
        {label} {optional ? <span className="font-normal" style={{ color: "var(--pp-muted)" }}>(optional)</span> : null}
      </label>
      <input
        id={field}
        name={field}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        onBlur={() => onBlur(field)}
        className={`pp-input ${error ? "error" : ""}`}
        data-testid={`input-${field}`}
      />
      {error ? (
        <span className="text-xs" style={{ color: "var(--pp-error)" }} data-testid={`error-${field}`}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export default function Step3Details({ details, onChange, errors, setErrors }) {
  const [touched, setTouched] = useState({});

  const update = (field, value) => {
    if (field === "owner_phone") value = formatPhone(value);
    onChange({ ...details, [field]: value });
    if (touched[field] || errors[field]) {
      setErrors({ ...errors, [field]: validateField(field, value) });
    }
  };

  const handleBlur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
    setErrors({ ...errors, [field]: validateField(field, details[field] ?? "") });
  };

  return (
    <div className="pp-step-fade" data-testid="step-3-details">
      <h2 className="text-xl md:text-2xl font-medium mb-1">Your details</h2>
      <p className="text-xs sm:text-sm mb-3" style={{ color: "var(--pp-muted)" }}>
        We'll send your booking confirmation to the email below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextField
          label="First name" field="owner_first_name" autoComplete="given-name" placeholder="Jane"
          value={details.owner_first_name} error={errors.owner_first_name} onChange={update} onBlur={handleBlur}
        />
        <TextField
          label="Last name" field="owner_last_name" autoComplete="family-name" placeholder="Doe"
          value={details.owner_last_name} error={errors.owner_last_name} onChange={update} onBlur={handleBlur}
        />
        <TextField
          label="Phone" field="owner_phone" type="tel" autoComplete="tel" placeholder="306-555-1234"
          value={details.owner_phone} error={errors.owner_phone} onChange={update} onBlur={handleBlur}
        />
        <TextField
          label="Email" field="owner_email" type="email" autoComplete="email" placeholder="you@example.com"
          value={details.owner_email} error={errors.owner_email} onChange={update} onBlur={handleBlur}
        />
        <TextField
          label="Pet name" field="pet_name" placeholder="Biscuit"
          value={details.pet_name} error={errors.pet_name} onChange={update} onBlur={handleBlur}
        />
        <TextField
          label="Pet breed" field="pet_breed" optional placeholder="Golden Retriever"
          value={details.pet_breed} error={errors.pet_breed} onChange={update} onBlur={handleBlur}
        />
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label className="text-sm font-medium" htmlFor="pet_notes" style={{ color: "var(--pp-ink)" }}>
          Notes for groomer <span className="font-normal" style={{ color: "var(--pp-muted)" }}>(optional, max 300 chars)</span>
        </label>
        <textarea
          id="pet_notes"
          name="pet_notes"
          rows={4}
          maxLength={300}
          value={details.pet_notes || ""}
          onChange={(e) => update("pet_notes", e.target.value)}
          onBlur={() => handleBlur("pet_notes")}
          className="pp-textarea"
          placeholder="Anxious around dryers, prefers a quiet space, etc."
          data-testid="input-pet_notes"
        />
        <div className="flex items-center justify-between text-xs" style={{ color: "var(--pp-muted)" }}>
          <span>{(details.pet_notes || "").length}/300</span>
          {errors.pet_notes ? (
            <span style={{ color: "var(--pp-error)" }} data-testid="error-pet_notes">{errors.pet_notes}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
